// facade take-off: perspective rectification (homography) + VOB deductions + materials + scaffolding
export const FAC_DEF = { paint: 0.18, coats: 2, primer: 0.15, bucket: 12.5, waste: 5, glue: 5, bag: 25, mesh: 10, dowels: 6, side: 1, top: 1, lift: 2, field: 2.5, anchor: 12, vob: 2.5 };

// solve A x = b (Gauss-Jordan)
function solve(A, b) {
  const n = b.length, M = A.map((r, i) => [...r, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    [M[c], M[p]] = [M[p], M[c]];
    if (Math.abs(M[c][c]) < 1e-12) return null;
    for (let r = 0; r < n; r++) if (r !== c) { const f = M[r][c] / M[c][c]; for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k]; }
  }
  return M.map((r, i) => r[n] / r[i]);
}

// 4 image points (clockwise from top-left) of a known w × h rectangle -> image->metre homography
export function homography(src, w, h) {
  const dst = [[0, 0], [w, 0], [w, h], [0, h]], A = [], b = [];
  src.forEach(([x, y], i) => {
    const [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  });
  const s = solve(A, b);
  return s && [...s, 1];
}
export const applyH = (H, [x, y]) => { const d = H[6] * x + H[7] * y + H[8]; return [(H[0] * x + H[1] * y + H[2]) / d, (H[3] * x + H[4] * y + H[5]) / d]; };

// calibration -> function image px -> metres
export function toMetric(cal) {
  if (cal?.mode === 'rect' && cal.pts?.length === 4 && +cal.w > 0 && +cal.h > 0) { const H = homography(cal.pts, +cal.w, +cal.h); return H && ((p) => applyH(H, p)); }
  if (cal?.mode === 'line' && cal.pts?.length === 2 && +cal.len > 0) { const [a, b] = cal.pts, k = +cal.len / Math.hypot(b[0] - a[0], b[1] - a[1]); return (p) => [p[0] * k, p[1] * k]; }
  return null;
}

const area = (P) => Math.abs(P.reduce((s, p, i) => { const q = P[(i + 1) % P.length]; return s + p[0] * q[1] - q[0] * p[1]; }, 0)) / 2;
const bbox = (P) => { const xs = P.map((p) => p[0]), ys = P.map((p) => p[1]); return { w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys), x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) }; };

export function facadeCalc(f, o = {}) {
  const c = { ...FAC_DEF, ...Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v != null && Number.isFinite(+v)).map(([k, v]) => [k, +v])) };
  const T = toMetric(f.cal);
  if (!T) return null;
  const polys = (f.polys || []).filter((p) => p.pts.length > 2).map((p) => { const m = p.pts.map(T), b = bbox(m); return { ...p, A: area(m), b }; });
  const areas = polys.filter((p) => p.kind === 'area'), cuts = polys.filter((p) => p.kind === 'cut');
  const gross = areas.reduce((s, p) => s + p.A, 0);
  const rows = cuts.map((p) => { const over = f.vob !== false && p.A < c.vob, dep = (+p.reveal || 0) / 100, lfm = dep > 0 ? 2 * p.b.h + p.b.w : 0; return { id: p.id, A: p.A, over, lfm, reveal: lfm * dep, w: p.b.w, h: p.b.h }; });
  const ded = rows.filter((r) => !r.over).reduce((s, r) => s + r.A, 0), reveal = rows.reduce((s, r) => s + r.reveal, 0), lfm = rows.reduce((s, r) => s + r.lfm, 0);
  const wall = gross - ded, paintA = wall + reveal;
  const all = areas.flatMap((p) => p.pts.map(T)), B = all.length ? bbox(all) : { w: 0, h: 0 };
  const sW = B.w + 2 * c.side, sH = B.h + c.top, sA = sW * sH, fields = Math.ceil(sW / c.field);
  return {
    gross, ded, wall, reveal, lfm, rows, paintA,
    paint: paintA * c.paint * c.coats, primer: paintA * c.primer, buckets: Math.ceil((paintA * c.paint * c.coats) / c.bucket),
    wdvs: wall * (1 + c.waste / 100), glue: wall * c.glue, bags: Math.ceil((wall * c.glue) / c.bag), mesh: wall * (1 + c.mesh / 100), dowels: Math.ceil(wall * c.dowels),
    width: B.w, eave: B.h, sW, sH, sA, lifts: Math.ceil(sH / c.lift), anchors: Math.ceil(sA / c.anchor), jacks: 2 * (fields + 1), net: sA,
  };
}
